import { Component, OnInit, Input, Output, EventEmitter, HostBinding } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IUserSelectorSettings } from '../user-selector-dlg.interface';
import { IChatMember } from 'src/app/chat/models';
import { UserAccountType, UserData } from 'src/app/data-types/user-data';
import { UserService } from 'src/app/services/user.service';
import { FormControl } from '@angular/forms';
import { debounceTime, map, of } from 'rxjs';
import { Store } from 'src/app/store';

@UntilDestroy()
@Component({
    selector: 'app-user-selector',
    templateUrl: './user-selector.component.html',
    styleUrls: ['./user-selector.component.scss'],
})
export class UserSelectorComponent implements OnInit {
    @Input() settingsData: IUserSelectorSettings;
    @Output() usersSelected = new EventEmitter<IChatMember | IChatMember[]>();
    @Output() selectionCanceled = new EventEmitter<void>();
    @Output() addNewLead = new EventEmitter<void>();
    @HostBinding('class') get class() {
        return this.style;
    }

    user = this.store.selectForLocal('user');

    allowToSelectMany: boolean;
    showTypeSelector = true;
    selectedUsers: IChatMember[] = [];
    selectedUserIds: string[] = [];
    buttonLabel: string;
    loading = false;
    searchName = new FormControl();
    users: UserData[] = [];
    filteredUsers: UserData[];
    selectedUserType = 'all';
    style: string;
    avatarSize: number;
    showCompnayName = true;
    showUserPhones = false;
    showUserEmail = false;

    userTypes = [
        { id: 'all', name: 'All' },
        { id: UserAccountType.Guest, name: 'Unknown' },
        { id: UserAccountType.Customer, name: 'Customer' },
        { id: UserAccountType.Trade, name: 'Trade' },
        { id: UserAccountType.Admin, name: 'Admin' },
        { id: UserAccountType.Courier, name: 'Courier' },
        { id: UserAccountType.Supplier, name: 'Supplier' },
    ];

    constructor(private userService: UserService, private store: Store) {}

    ngOnInit(): void {
        this.allowToSelectMany = this.settingsData.mode === 'many';
        if (this.settingsData.showTypeSelector !== undefined && this.settingsData.showTypeSelector !== null) {
            this.showTypeSelector = this.settingsData.showTypeSelector;
        }
        if (this.settingsData.selectedUsers?.length) {
            this.selectedUsers = this.mapUsers(this.settingsData.selectedUsers);
            this.selectedUserIds = this.selectedUsers.map((u) => u.id);
        }
        this.buttonLabel = this.settingsData.label ? this.settingsData.label : 'Apply';
        this.style = this.settingsData.style;

        if (this.settingsData.avatarSize) {
            this.avatarSize = this.settingsData.avatarSize;
        }

        if (this.settingsData.showCompnayName !== undefined) {
            this.showCompnayName = this.settingsData.showCompnayName;
        }

        if (this.settingsData.showUserPhones || this.settingsData.phoneEmailTopToggle) {
            this.showUserPhones = true;
        }

        if (this.settingsData.showUserEmail) {
            this.showUserEmail = this.settingsData.showUserEmail;
        }

        this.loadUsers();

        this.searchName.valueChanges
            .pipe(untilDestroyed(this))
            .pipe(debounceTime(300))
            .subscribe(() => {
                this.filterUsers();
            });
    }

    selectUsers() {
        const selectedUsers = this.allowToSelectMany ? this.selectedUsers : this.selectedUsers[0];
        this.usersSelected.emit(selectedUsers);
    }

    userSelected(user: IChatMember): boolean {
        if (this.settingsData.selectByClick) {
            this.usersSelected.emit(user);
        }

        const i = this.selectedUserIds.indexOf(user.id);

        // we don't allow to select more then one user for chat model)
        if (this.settingsData.mode === 'single') {
            this.selectedUsers.splice(0, this.selectedUsers.length);
            this.selectedUserIds = this.selectedUsers.map((u) => u.id);
        }

        if (i === -1) {
            this.selectedUsers.push(user as any);
            this.selectedUserIds = this.selectedUsers.map((u) => u.id);
            return true;
        } else {
            this.selectedUsers.splice(i, 1);
            this.selectedUserIds = this.selectedUsers.map((u) => u.id);
            return false;
        }
    }

    onCheckBoxClick(user, $event) {
        this.userSelected(user);
        $event.stopPropagation();
        return true;
    }

    onRadioButtonClick(user, $event) {
        this.userSelected(user);
        $event.preventDefault();
        $event.stopPropagation();
        return true;
    }

    filterUsers() {
        const searchName: string = this.searchName.value;
        this.filteredUsers = this.users.filter((u) => {
            // showing only allowed types
            if (
                this.settingsData.allowedUserTypes?.length &&
                !this.settingsData.allowedUserTypes.includes(u.accountType)
            ) {
                return false;
            }

            // showing only currently selected type
            if (this.selectedUserType !== 'all' && u.accountType !== this.selectedUserType) {
                return false;
            }
            if (searchName) {
                const fullname = u.contact.firstname + ' ' + u.contact.surname;
                return (
                    fullname.toLowerCase().includes(searchName.toLowerCase()) ||
                    this.getCompanyName(u)?.toLowerCase().includes(searchName.toLowerCase()) ||
                    (this.showUserPhones &&
                        u.contact.mobile &&
                        u.contact.mobile.toLowerCase().replace(/ /g, '').includes(searchName.toLowerCase())) ||
                    (this.showUserEmail &&
                        u.contact.email &&
                        u.contact.email.toLowerCase().replace(/ /g, '').includes(searchName.toLowerCase()))
                );
            }
            return true;
        });
    }

    cancel() {
        this.selectionCanceled.emit();
    }

    setShowUserPhones() {
        this.showUserPhones = true;
        this.showUserEmail = false;
    }

    setShowUserEmail() {
        this.showUserEmail = true;
        this.showUserPhones = false;
    }

    private loadUsers() {
        this.loading = true;
        this.getUsers()
            .pipe(untilDestroyed(this))
            .subscribe((users) => {
                users.map((u) => {
                    u.contact = {
                        id: u.contactId,
                        firstname: u.firstname,
                        surname: u.surname,
                        mobile: u.mobile,
                        email: u.email,
                    };
                });
                users.sort((a, b) => {
                    return (a.firstname + a.surname).localeCompare(b.firstname + b.surname);
                });

                this.users = users;
                this.filteredUsers = this.users;
                this.filterUsers();
                this.loading = false;
            });
    }

    private getUsers() {
        const allUsers = this.store.selectForLocal('allUsers');
        if (allUsers.length) {
            return of(allUsers);
        }
        return this.userService.all().pipe(map((res) => res.data));
    }

    private mapUsers(users: IChatMember[] | UserData[]): IChatMember[] {
        return users.map((u) => {
            if (u.contact && !u.firstname) {
                u.firstname = u.contact.firstname;
                u.surname = u.contact.surname;
            }
            return u;
        });
    }

    private getCompanyName(user): string {
        return user.companyName || user.prospectCompanyName;
    }
}
